/**
 * Whether a page reloads when a new service worker takes it over (`startOffline` in client.ts).
 *
 * The worker in control serves the shell it precached, so the first load after a deploy runs the
 * previous build against the new API until the new worker — installed at once, `skipWaiting` and
 * `clients.claim()` in sw.ts — takes the page over. On 2026-10-28 (after !204) that build met a
 * product value it did not know and crashed the room. A reload onto the new shell is right only
 * when nothing can be lost: the page was already controlled (a first install is not a takeover),
 * it is young, and the person has not touched it — a pointer, a key or an input event since the
 * load means a draft, a scroll position or a focused field that a reload would throw away. The
 * page then keeps running; the next load gets the new shell.
 */

/** How young a load still reloads when a new worker takes it over. */
export const RELOAD_ON_TAKEOVER_MS = 20_000;

export interface TakeoverState {
  /** A worker controlled the page when it loaded. */
  hadController: boolean;
  /** Milliseconds since the load (`performance.now()`). */
  ageMs: number;
  /** A pointer, key or input event has reached the page since the load. */
  interacted: boolean;
}

export function shouldReloadOnTakeover(s: TakeoverState, limitMs: number = RELOAD_ON_TAKEOVER_MS): boolean {
  return s.hadController && !s.interacted && s.ageMs >= 0 && s.ageMs < limitMs;
}

/** The events whose first occurrence marks the page as touched. */
export const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'input', 'touchstart'] as const;

/**
 * Watches the page for its first interaction; `touched()` reads it. Listening stops at the first one.
 */
export function watchInteraction(target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>): { touched: () => boolean } {
  let interacted = false;
  const onFirst = () => {
    interacted = true;
    for (const name of INTERACTION_EVENTS) target.removeEventListener(name, onFirst, true);
  };
  for (const name of INTERACTION_EVENTS) target.addEventListener(name, onFirst, { capture: true, passive: true });
  return { touched: () => interacted };
}
